import axios from 'axios';

const API_URL =
  process.env.REACT_APP_API_URL ||
  'https://yvui056ywi.execute-api.us-east-1.amazonaws.com/prototype';

export const apiClient = axios.create({
  baseURL:  API_URL,
  timeout:  30000,
});

// Attach token from sessionStorage as Authorization header (required by
// API Gateway's Cognito authorizer). The HttpOnly cookie is sent in
// parallel for defense-in-depth. A future custom Lambda authorizer can
// remove the Authorization header requirement.
apiClient.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('iad2_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// On 401: clear session and redirect to login
apiClient.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      sessionStorage.removeItem('iad2_token');
      sessionStorage.removeItem('iad2_user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// API helpers
export const api = {
  // Auth
  me: () => apiClient.get('/users/me').then((r) => r.data.data),

  // Settings (verification thresholds — E77)
  getSettings: (): Promise<Record<string, string>> =>
    apiClient.get('/settings').then((r) => r.data.data),
  updateSettings: (data: { verification_aging_days?: number; verification_overdue_days?: number }) =>
    apiClient.put('/settings', data).then((r) => r.data),

  // Contacts
  searchContacts: (params: Record<string, string | number | boolean | undefined>) =>
    apiClient.get('/contacts', { params }).then((r) => r.data),
  getContact: (id: string) => apiClient.get(`/contacts/${id}`).then((r) => r.data.data),
  createContact: (data: unknown) => apiClient.post('/contacts', data).then((r) => r.data),
  updateContact: (id: string, data: unknown) =>
    apiClient.put(`/contacts/${id}`, data).then((r) => r.data),
  flagContact: (id: string, data: unknown) =>
    apiClient.post(`/contacts/${id}/flag`, data).then((r) => r.data),
  archiveContact: (id: string, reason?: string) =>
    apiClient.post(`/contacts/${id}/archive`, { reason }).then((r) => r.data),
  restoreContact: (id: string) =>
    apiClient.post(`/contacts/${id}/restore`, {}).then((r) => r.data),
  markContactVerified: (id: string) =>
    apiClient.post(`/contacts/${id}/verify`, {}).then((r) => r.data),
  sendContact: (id: string, data: unknown) =>
    apiClient.post(`/contacts/${id}/send`, data).then((r) => r.data),
  sendContacts: (data: { contact_ids: string[]; recipients: string[]; message?: string | null }) =>
    apiClient.post(`/contacts/${data.contact_ids[0]}/send`, data).then((r) => r.data),
  sendList: (listId: string, recipients: string[], message?: string | null) =>
    apiClient.post(`/lists/${listId}/send`, { recipients, message: message ?? null }).then((r) => r.data),

  // Agencies
  getAgencies: (params?: Record<string, string | number | undefined>) =>
    apiClient.get('/agencies', { params }).then((r) => r.data),
  getAgency: (id: string) => apiClient.get(`/agencies/${id}`).then((r) => r.data.data),
  createAgency: (data: unknown) => apiClient.post('/agencies', data).then((r) => r.data),
  updateAgency: (id: string, data: unknown) =>
    apiClient.put(`/agencies/${id}`, data).then((r) => r.data),
  archiveAgency: (id: string) => apiClient.delete(`/agencies/${id}`).then((r) => r.data),
  reactivateAgency: (id: string) => apiClient.post(`/agencies/${id}/reactivate`, {}).then((r) => r.data),

  // Roles
  getRoles: (params?: { include_inactive?: boolean }) =>
    apiClient.get('/roles', { params }).then((r) => r.data.data),
  createRole: (data: { name: string; description?: string }) =>
    apiClient.post('/roles', data).then((r) => r.data),
  updateRole: (id: string, data: { name?: string; description?: string; is_active?: boolean }) =>
    apiClient.patch(`/roles/${id}`, data).then((r) => r.data),
  deactivateRole: (id: string) => apiClient.delete(`/roles/${id}`).then((r) => r.data),
  getContactRoles: (contactId: string) =>
    apiClient.get(`/contacts/${contactId}/roles`).then((r) => r.data.data),
  assignRoles: (contactId: string, roleIds: string[]) =>
    apiClient.put(`/contacts/${contactId}/roles`, { role_ids: roleIds }).then((r) => r.data.data),

  // Lists
  getLists: (params?: Record<string, string | undefined>) => apiClient.get('/lists', { params }).then((r) => r.data),
  getList: (id: string) => apiClient.get(`/lists/${id}`).then((r) => r.data.data),
  resolveList: (id: string) => apiClient.get(`/lists/${id}/resolve`).then((r) => r.data),
  createList: (data: unknown) => apiClient.post('/lists', data).then((r) => r.data),
  updateList: (id: string, data: unknown) => apiClient.put(`/lists/${id}`, data).then((r) => r.data),
  addListMember: (listId: string, contactId: string) =>
    apiClient.post(`/lists/${listId}/members`, { contact_id: contactId, is_manual_add: true, is_manual_exclude: false }).then((r) => r.data),
  removeListMember: (listId: string, contactId: string) =>
    apiClient.delete(`/lists/${listId}/members/${contactId}`).then((r) => r.data),

  // System power control. These two are the ONLY endpoints that work while the database is
  // stopped — authorization is validated by the API Gateway Cognito authorizer, and their
  // handlers never query Postgres. See docs/RDS_IDLE_SPINDOWN.md.
  getSystemStatus: () => apiClient.get('/system/status').then((r) => r.data),
  wakeSystem:      () => apiClient.post('/system/wake').then((r) => r.data),

  // Flags
  getFlags: (params?: Record<string, string | undefined>) =>
    apiClient.get('/flags', { params }).then((r) => r.data),
  resolveFlag: (id: string, data: unknown) =>
    apiClient.post(`/flags/${id}/resolve`, data).then((r) => r.data),

  // Pending changes
  getPendingChanges: (params?: Record<string, string | number | undefined>) =>
    apiClient.get('/pending-changes', { params }).then((r) => r.data),
  approvePendingChange: (id: string) =>
    apiClient.post(`/pending-changes/${id}/approve`, {}).then((r) => r.data),
  rejectPendingChange: (id: string, note: string) =>
    apiClient
      .post(`/pending-changes/${id}/reject`, { resolution_note: note })
      .then((r) => r.data),

  // Update requests
  getUpdateRequests: (params?: Record<string, string | number | boolean>) =>
    apiClient.get('/update-requests', { params }).then((r) => r.data),
  createUpdateRequest: (data: unknown) =>
    apiClient.post('/update-requests', data).then((r) => r.data),
  cancelUpdateRequest: (id: string) =>
    apiClient.post(`/update-requests/${id}/cancel`, {}).then((r) => r.data),

  // KPI
  getKPI: () => apiClient.get('/kpis').then((r) => r.data.data),

  // Audit log
  getAuditLog: (params?: Record<string, string | number | undefined>) =>
    apiClient.get('/audit-log', { params }).then((r) => r.data),

  // Users
  getUsers: (params?: { is_active?: string }) => apiClient.get('/users', { params }).then((r) => r.data),
  createUser: (data: unknown) => apiClient.post('/users', data).then((r) => r.data),
  updateUser: (id: string, data: unknown) =>
    apiClient.put(`/users/${id}`, data).then((r) => r.data),
  deactivateUser: (id: string) => apiClient.delete(`/users/${id}`).then((r) => r.data),
  reactivateUser: (id: string) =>
    apiClient.post(`/users/${id}/reactivate`, {}).then((r) => r.data),
  resetPassword: (id: string) =>
    apiClient.post(`/users/${id}/reset-password`, {}).then((r) => r.data),
  grantRestricted: (id: string) =>
    apiClient.post(`/users/${id}/grant-restricted`, {}).then((r) => r.data),

  // PDF Export
  exportContactPDF: (id: string) =>
    apiClient.post(`/exports/contact/${id}`).then((r) => r.data),
  exportAgencyPDF: (id: string) =>
    apiClient.post(`/exports/agency/${id}`).then((r) => r.data),
  exportDirectoryPDF: (params?: { q?: string; agency_id?: string; role_id?: string; verification?: string; active_only?: string }) => {
    const qs = params ? '?' + new URLSearchParams(Object.entries(params).filter(([, v]) => v != null) as [string, string][]).toString() : '';
    return apiClient.post(`/exports/directory${qs}`).then((r) => r.data);
  },
  getPrintPreview: () => apiClient.get('/exports/preview').then((r) => r.data.data),

  // Import
  importContacts: (data: unknown) => apiClient.post('/import', data).then((r) => r.data),

  // Update portal (public — no auth header needed for these)
  verifyPortalToken: (token: string, pin: string) =>
    apiClient.post('/update-requests/verify', { token, pin }).then((r) => r.data),
  getPortalContacts: (token: string) =>
    apiClient.get(`/update-requests/${token}/contacts`).then((r) => r.data),
  submitPortalContact: (token: string, contactId: string, data: unknown) =>
    apiClient
      .patch(`/update-requests/${token}/contacts/${contactId}`, data)
      .then((r) => r.data),
  submitPortal: (token: string) =>
    apiClient.post(`/update-requests/${token}/submit`).then((r) => r.data),

  // HR Sync log
  getHRSyncLog: () => apiClient.get('/integrations/hr/log').then((r) => r.data),

  // Login audit (fire-and-forget — called from AuthContext after successful login)
  logLogin: () => apiClient.post('/users/me/login').then((r) => r.data),
};
